import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "lucide-react";

/**
 * A lightweight HTML5 canvas signature pad (mouse + touch). No external deps.
 * Exposes isEmpty() and toDataURL() via ref; calls onChange(hasInk) as the
 * user draws so the parent can enable/disable the save button.
 */
const SignaturePad = forwardRef(function SignaturePad({ onChange, height = 180 }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  useImperativeHandle(ref, () => ({
    isEmpty: () => !hasInk,
    toDataURL: () => canvasRef.current?.toDataURL("image/png"),
    clear: () => clearCanvas(),
  }));

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
  };

  useEffect(() => {
    setupCanvas();
    const onResize = () => {
      // Re-init on resize wipes the pad; acceptable for a transient capture modal.
      setupCanvas();
      setHasInk(false);
      onChange && onChange(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) {
      setHasInk(true);
      onChange && onChange(true);
    }
  };

  const end = () => {
    drawing.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange && onChange(false);
  };

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        style={{ height, touchAction: "none", background: "#ffffff" }}
        className="w-full rounded-lg border-2 border-dashed border-slate-300 cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {!hasInk && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-[12.5px] text-slate-400">Sign here with your mouse or finger</span>
        </div>
      )}
      <button
        type="button"
        onClick={clearCanvas}
        data-testid="signature-clear"
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 border border-slate-200 text-[11px] text-slate-600 hover:text-slate-900 hover:border-slate-300"
      >
        <Eraser className="h-3 w-3" /> Clear
      </button>
    </div>
  );
});

export default SignaturePad;
